import { useMyLocation } from '@/services/useLocations';
import { useSelectedDate } from '@/services/useSelectedDate';
import { useCallback, useMemo } from 'react';
import { dateStringToApiFormat, dateToApiFormat, secondsToHours } from '../../../helpers/dateHelper';
import { FixedLeaveTypeId } from '../../../services/availability/availability.types';
import { useGetContract } from '../../../services/contract/useGetContract';
import { useGetContractName } from '../../../services/contract/useGetContractName';
import { useGetContractRole } from '../../../services/contract/useGetContractRole';
import { ReadEmployee, ReadEmployeeSkills } from '../../../services/gql/graphql.generated';
import { useGetDayOfWeekAndHoliday } from '../../../services/holiday/usePublicHolidays';
import { useSystemSettings } from '../../../services/settings/SystemSettingsProvider';
import strings from '../roster.translations';
import { WorkingShift } from '../../../types/daily.types';
import { AllocationStatus, EmployeeForShiftItem, EmployeeStatus } from '../../../types/employeeForShift.types';
import { useRosterAllEmployeesSummary, useWorkingRoster } from '../providers/WorkingRosterService';
import { useAllEmployeeOptions } from './useAllEmployeeOptions';
import { useOtherShopShiftsForCurrentRoster } from './useGetDayShiftsOfStaffFromOtherShops';
import { useRosterStatuses } from './useRosterStatuses';
import { useSortEmployees } from './useSortEmployees';
import { useTaskMatch } from './useTaskMatch';
import { RosterGeneralShift, getAvailableSlots, getSelectedAvailableSlot, getTimeAvailable } from '../rosterHelper';

export const displayNameWithShiftCount = (
  name: string,
  currentShift: WorkingShift,
  shiftLimit: number,
  assignedShifts?: WorkingShift[]
) => {
  if (!assignedShifts || assignedShifts.length === 0 || shiftLimit === assignedShifts.length) {
    return name;
  }

  let shiftIndex = assignedShifts.findIndex(s => s.id === currentShift.id);
  if (shiftIndex < 0) {
    shiftIndex = assignedShifts.length;
  }

  if (shiftIndex === 0) {
    return name;
  } else {
    return `${name} +${shiftIndex}`;
  }
};

export const getStatus = (shift: WorkingShift, availableSlot?: AvailableShiftSlot): EmployeeStatus => {
  if (
    !availableSlot ||
    shift?.start === undefined ||
    shift?.end === undefined ||
    availableSlot.start === availableSlot.end
  ) {
    return EmployeeStatus.NOT_AVAILABLE;
  }

  if (availableSlot.start <= shift.start && availableSlot.end >= shift.end) {
    return EmployeeStatus.AVAILABLE;
  }

  let partiallyAvailable = true;
  if (availableSlot.end < shift.end && !availableSlot.partiallyEnforcedEnd) {
    partiallyAvailable = false;
  }

  if (availableSlot.start > shift.start && !availableSlot.partiallyEnforcedStart) {
    partiallyAvailable = false;
  }

  if (partiallyAvailable) {
    return EmployeeStatus.PARTLY_AVAILABLE;
  }

  return EmployeeStatus.NOT_AVAILABLE;
};

export interface AvailableShiftSlot {
  start: number;
  end: number;
  partiallyEnforcedStart?: boolean;
  partiallyEnforcedEnd?: boolean;
}

export const useBuildEmployeesForShift = () => {
  const summary = useRosterAllEmployeesSummary();
  const { workingRoster } = useWorkingRoster();

  const getContractName = useGetContractName();
  const getContract = useGetContract();
  const getContractRole = useGetContractRole();

  const selectedDate = useSelectedDate();
  const selectedDateString = dateToApiFormat(selectedDate);

  const selectedLocationId = useMyLocation().id;
  const rosterStatuses = useRosterStatuses(selectedDate, selectedLocationId, summary);
  const dayShiftsOfStaffFromOtherShops = useOtherShopShiftsForCurrentRoster();

  const reassignment = useSystemSettings(state => state.settings.reassignment);

  const isHomeStaffOrRosterableStaffFromOtherShops = useCallback(
    (employee: ReadEmployee) => {
      // the staff member at the home location is always rosterable.
      if (employee.homeLocationId === selectedLocationId) {
        return true;
      }

      // the staff member from other shop, who is already assigned to a shift on the day, is not rosterable.
      if (
        dayShiftsOfStaffFromOtherShops?.find(
          x =>
            x.identityId === employee.identityId &&
            x.shifts!.filter(s => s.status === 'Published' || s.status === 'Republished').length > 0
        )
      ) {
        return false;
      }

      // the staff member from other shop is rosterable in the on-request model even if a roster of the shop hasn't been published.
      if (reassignment.model === 'on-request') {
        return true;
      }

      // staff from other shop are rosterable if their home rosters are published
      return (
        rosterStatuses?.find(s => {
          const rosterDate = dateStringToApiFormat(s.rosterDate as string);
          return (
            s.locationId === employee.homeLocationId &&
            rosterDate === selectedDateString &&
            (s.status === 'Published' || s.status === 'Republished')
          );
        }) !== undefined
      );
    },
    [rosterStatuses, dayShiftsOfStaffFromOtherShops, selectedLocationId, reassignment, selectedDateString]
  );

  const { awayEmployees, employeesSkillsList } = useAllEmployeeOptions();

  const { maxNumber: maxShifts, minSecondsBetween: secondsBetween } = useSystemSettings(
    state => state.settings.splitShifts
  );

  const taskMatch = useTaskMatch();

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const getDayOfWeekAndHoliday = useGetDayOfWeekAndHoliday(selectedDate);

  const sortEmployees = useSortEmployees();

  return useCallback(
    (shift: WorkingShift, reassignedEmployees: string[] = []) => {
      const getTaskMatch = taskMatch(shift);

      let employees: EmployeeForShiftItem[] = [];

      summary.forEach(employee => {
        const schedule = employee.schedule.find(s => s.date === selectedDateString);
        const skills = (employeesSkillsList.find(e => e.employeeId === employee.id)?.skills ||
          []) as ReadEmployeeSkills[];

        /*
         * Weird bit:
         *
         * We need to find the when an employee can work, by looking at their shifts and finding an
         * available gap.
         *
         * This requires us to look at both their shifts from the working roster (the one that's being
         * edited currently) and also their ghost shifts, which are shifts from the previous or next day
         * which hang over into this day.
         *
         * These are two entirely different types, so we have to cast them to the RosterGeneralShift
         * type, which has the relevant common fields.
         */
        const workingRosterShifts =
          workingRoster?.shifts.filter(s => s.assignedEmployeeId === employee.id) || ([] as RosterGeneralShift[]);
        const ghostShifts = schedule?.ghostShifts || ([] as RosterGeneralShift[]);

        const shifts: RosterGeneralShift[] = [...workingRosterShifts, ...ghostShifts];

        let availableSlots: AvailableShiftSlot[] = [];

        schedule?.availableTimes?.forEach(availableTime => {
          if (!shifts) {
            availableSlots.push(availableTime);
          } else {
            availableSlots = [
              ...availableSlots,
              ...getAvailableSlots(shifts, availableTime.start, availableTime.end, secondsBetween)
            ];
          }
        });

        const selectedAvailableSlot = getSelectedAvailableSlot(availableSlots, shift?.start, shift?.end);

        const assignedShifts = workingRoster?.shifts.filter(shift => shift.assignedEmployeeId === employee.id) || [];

        const status: EmployeeStatus =
          shift.assignedEmployeeId === employee.id ? EmployeeStatus.AVAILABLE : getStatus(shift, selectedAvailableSlot);

        let allocationStatus = AllocationStatus.NOT_ALLOCATED;
        if (assignedShifts.length >= maxShifts) {
          allocationStatus = AllocationStatus.FULLY_ALLOCATED;
        } else if (assignedShifts.length > 0) {
          allocationStatus = AllocationStatus.PARTLY_ALLOCATED;
        }

        let timeAvailable: string[] = [getTimeAvailable({ start: 0, end: 0 })];

        if (schedule?.availableTimes && schedule.availableTimes.length > 0) {
          timeAvailable = schedule.availableTimes.map(t => getTimeAvailable(t));
        }

        const contractName = getContractName(employee.contractId);
        const roleName = getContractRole(employee.details.contractRoleId);

        const { taskMatch, impossibleTasks } = getTaskMatch(skills);

        const dropdownItem: EmployeeForShiftItem = {
          id: employee.id,
          minorIssues: [],
          majorIssues: [],
          name: displayNameWithShiftCount(employee.name, shift, maxShifts, assignedShifts),
          contractId: employee.contractId,
          status,
          timeAvailable,
          contract: contractName,
          leaveType: schedule?.leaveType,
          allocated: allocationStatus,
          assignable: true,
          employeeId: employee.employeeId,
          details: employee.details,
          skills: skills,
          taskMatch,
          employeeSummary: employee
        };

        if (dropdownItem.status === EmployeeStatus.PARTLY_AVAILABLE) {
          dropdownItem.minorIssues.push(strings.staffSelector.warnings.partialAvailability);
        }

        // If allocated already, can't be reassigned.
        if (
          shift.assignedEmployeeId !== dropdownItem.id &&
          dropdownItem.allocated === AllocationStatus.FULLY_ALLOCATED
        ) {
          dropdownItem.majorIssues.push(strings.staffSelector.warnings.alreadyAllocated);
        } else {
          if (impossibleTasks.length > 0) {
            dropdownItem.majorIssues.push(
              `This employee lacks the skills to be assigned to a shift with '${impossibleTasks[0].displayName}'.`
            );
          }

          // If the availability state is a fixed shift, we need to override all availability information.
          if (dropdownItem.leaveType === FixedLeaveTypeId.ROSTER) {
            dropdownItem.majorIssues.push(strings.staffSelector.warnings.rdo);
            dropdownItem.status = EmployeeStatus.NOT_AVAILABLE;
            dropdownItem.timeAvailable = ['00:00 - 00:00'];
          } else if (dropdownItem.status === EmployeeStatus.NOT_AVAILABLE) {
            dropdownItem.majorIssues.push(strings.staffSelector.warnings.noAvailability);
          } else {
            // If the shift duration does not honour the employees contract terms, the employee should be made unavailable.
            const contract = getContract(employee.contractId);
            if (contract) {
              const contractMaxShiftDuration = contract.maxHoursPerShift;
              const shiftDuration = secondsToHours((shift.end ?? 0) - (shift.start ?? 0));
              if (shiftDuration > contractMaxShiftDuration) {
                dropdownItem.status = EmployeeStatus.NOT_AVAILABLE;
                dropdownItem.majorIssues.push(strings.staffSelector.warnings.exceedsShiftMax);
              } else {
                const contractMaxDayHours = contract.maxHoursPerDay;
                const otherShifts =
                  workingRoster?.shifts.filter(s => s.assignedEmployeeId === employee.id && s.id !== shift.id) || [];

                const otherShiftsDuration = otherShifts?.reduce((prev, shift) => {
                  return prev + (shift?.end || 0) - (shift?.start || 0);
                }, 0);
                const dayHours = shiftDuration + secondsToHours(otherShiftsDuration);

                if (contractMaxDayHours && dayHours > contractMaxDayHours) {
                  dropdownItem.status = EmployeeStatus.NOT_AVAILABLE;
                  dropdownItem.majorIssues.push(strings.staffSelector.warnings.exceedsDayMax);
                }
              }
            }
          }
        }

        if (dropdownItem.majorIssues.length > 0) {
          dropdownItem.assignable = false;
        }

        /*
       NOTE: THIS MUST BE AT THE END OF THE CHECKS!!!
       */
        // Exception- if a staff member is already assigned to this shift, they need to be assignable.
        // Attempting to assign someone twice causes an un-assign, so we need this to undo cases where someone's accidentally
        // been assigned when they're unavailable.
        if (shift.assignedEmployeeId === dropdownItem.id) {
          dropdownItem.assignable = true;
        }

        employees.push(dropdownItem);
      });

      employees = employees.filter(employee => {
        // Always include the assigned employee. We need to display their name if they're selected.
        if (shift.assignedEmployeeId === employee.id) {
          return true;
        }

        // Remove staff who are rostered at other shops.
        if (!isHomeStaffOrRosterableStaffFromOtherShops(employee.details!)) {
          return false;
        }

        // Remove staff from other shops who aren't available.
        if (employee.details?.homeLocationId !== selectedLocationId && employee.status !== EmployeeStatus.AVAILABLE) {
          return false;
        }

        // Remove any staff who have already left.
        const leaveDate = employee.employeeSummary?.settings.leftCompanyDate;

        if (leaveDate && leaveDate < selectedDate) {
          // If the employee has left, filter them out.
          return false;
        }

        return true;
      });

      // Add items for reassigned staff from other shops
      // awayEmployees.forEach(employee => {
      //   const skills = (employeesSkillsList.find(e => e.employeeId === employee.identityId)?.skills ||
      //     []) as ReadEmployeeSkills[];

      //   employees.push({
      //     id: employee.identityId,
      //     employeeId: employee.employeeId || '',
      //     name: displayName(employee),
      //     contractId: employee.contract?.contractTypeId,
      //     status: EmployeeStatus.AVAILABLE, //TODO: Must be fully available?
      //     timeAvailable: [getTimeAvailable({ start: shift.start || 0, end: shift.end || 0 })], //TODO: Does time available match the time slot of the shift?
      //     role: employee.role,
      //     leaveType: undefined, //TODO: Is this important for reassigned staff?
      //     allocated: AllocationStatus.FULLY_ALLOCATED,
      //     assignable: true,
      //     skills: skills,
      //     taskMatch: getTaskMatch(skills),
      //     details: employee
      //   });
      // });

      return sortEmployees(employees, shift);
    },
    [
      taskMatch,
      summary,
      awayEmployees,
      sortEmployees,
      employeesSkillsList,
      workingRoster,
      maxShifts,
      getContractName,
      getContractRole,
      getContract,
      selectedDateString,
      secondsBetween,
      isHomeStaffOrRosterableStaffFromOtherShops,
      selectedLocationId,
      selectedDate
    ]
  );
};

export const useEmployeesForShift = (shift: WorkingShift, reassignedEmployees: string[] = []) => {
  const buildAvailableEmployees = useBuildEmployeesForShift();

  return useMemo(
    () => buildAvailableEmployees(shift, reassignedEmployees),
    [shift, reassignedEmployees, buildAvailableEmployees]
  );
};
